import { Controller } from "@hotwired/stimulus"
import { escapeHtml, parseJsonScript } from "../utils/dom"

export default class extends Controller {
  static targets = ["programmeList", "pairsJson", "airlinesData", "addSelect"]
  static values = {
    initialPairs: { type: Array, default: [] }
  }

  connect() {
    this.pairs = []
    const initial = Array.isArray(this.initialPairsValue) ? this.initialPairsValue : []
    initial.forEach((p) => {
      const a = p?.airline ?? p?.["airline"]
      const t = p?.tier ?? p?.["tier"]
      if (a && t) this.pairs.push({ airline: String(a), tier: String(t) })
    })
    this.addedProgrammes = new Set(this.pairs.map((p) => p.airline))
    this.fillAddSelectOptions()
    this.render()
    this.syncHiddenJson()
  }

  fillAddSelectOptions() {
    const sel = this.addSelectTarget
    const data = this.airlines
    const prev = sel.value
    sel.innerHTML = ""
    const opt0 = document.createElement("option")
    opt0.value = ""
    opt0.textContent = "Add a loyalty program…"
    sel.appendChild(opt0)
    Object.keys(data)
      .sort((a, b) => (data[a]?.name || a).localeCompare(data[b]?.name || b))
      .forEach((key) => {
        if (this.addedProgrammes.has(key)) return
        const o = document.createElement("option")
        o.value = key
        o.textContent = data[key]?.name || key
        sel.appendChild(o)
      })
    if (prev && [...sel.options].some((o) => o.value === prev)) sel.value = prev
  }

  get airlines() {
    return parseJsonScript(this.hasAirlinesDataTarget ? this.airlinesDataTarget : null) || {}
  }

  addProgramme(event) {
    const sel = event.currentTarget
    const key = sel.value
    if (!key) return
    this.addedProgrammes.add(key)
    sel.value = ""
    this.fillAddSelectOptions()
    this.render()
    this.syncHiddenJson()
  }

  removeProgramme(event) {
    const key = event.currentTarget.dataset.airlineKey
    if (!key) return
    this.addedProgrammes.delete(key)
    this.pairs = this.pairs.filter((p) => p.airline !== key)
    this.fillAddSelectOptions()
    this.render()
    this.syncHiddenJson()
  }

  toggleTier(event) {
    event.preventDefault()
    const airlineKey = event.currentTarget.dataset.airlineKey
    const tier = event.currentTarget.dataset.tier
    if (!airlineKey || !tier) return
    const idx = this.pairs.findIndex((p) => p.airline === airlineKey && p.tier === tier)
    if (idx >= 0) {
      this.pairs.splice(idx, 1)
    } else {
      this.pairs.push({ airline: airlineKey, tier })
    }
    this.render()
    this.syncHiddenJson()
  }

  render() {
    const data = this.airlines
    const keys = [...this.addedProgrammes].sort((a, b) =>
      (data[a]?.name || a).localeCompare(data[b]?.name || b)
    )

    if (keys.length === 0) {
      this.programmeListTarget.innerHTML =
        '<p class="text-sm text-base-content/60">Use &ldquo;Add a loyalty program&rdquo; to include a source program, then choose which of its tiers qualify.</p>'
      return
    }

    this.programmeListTarget.innerHTML = keys
      .map((airlineKey) => {
        const meta = data[airlineKey]
        const name = escapeHtml(meta?.name || airlineKey)
        const tiers = meta?.tiers || []
        const chips = tiers
          .map((tier) => {
            const on = this.pairs.some((p) => p.airline === airlineKey && p.tier === tier)
            const cls = on ? "btn-primary border-primary" : "btn-outline"
            return `
            <button type="button"
              class="btn btn-sm ${cls} px-3 py-2 transition-all duration-150"
              data-airline-key="${escapeHtml(airlineKey)}"
              data-tier="${escapeHtml(tier)}"
              data-action="click->campaign-eligibility#toggleTier">
              ${escapeHtml(tier)}
            </button>`
          })
          .join("")

        return `
        <div class="rounded-xl border border-base-300 bg-base-100 p-3 sm:p-4 space-y-3 shadow-sm">
          <div class="flex flex-wrap items-start justify-between gap-2">
            <div class="min-w-0">
              <p class="text-sm font-semibold text-base-content">${name}</p>
              <p class="text-xs text-base-content/60 mt-0.5">Only tiers you select here count for this program.</p>
            </div>
            <button type="button" class="btn btn-ghost btn-xs text-error shrink-0"
              data-airline-key="${escapeHtml(airlineKey)}"
              data-action="click->campaign-eligibility#removeProgramme">
              Remove
            </button>
          </div>
          <div class="flex flex-wrap gap-2" role="group" aria-label="${name}">
            ${chips || '<p class="text-sm text-base-content/60">No tiers defined for this program.</p>'}
          </div>
        </div>`
      })
      .join("")
  }

  syncHiddenJson() {
    const payload = this.pairs.map((p) => ({ airline: p.airline, tier: p.tier }))
    this.pairsJsonTarget.value = JSON.stringify(payload)
  }
}
